interface ILockedParams {
    container?: HTMLElement;
    activeElement?: HTMLElement;
}

const NEIGHBOURS_TAGS_TO_EXCLUDE = ["SCRIPT", "STYLE"];

const INITIAL_ARIA_HIDDEN_ATTR = "initial-aria-hidden";

const FOCUSABLE_ELEMENT_SELECTOR = `a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]),
    iframe, object, embed, textarea, [tabindex]:not([tabindex^='-']), [contenteditable]`;

export function getFocusableItems(container: HTMLElement) {
    return container.querySelectorAll(FOCUSABLE_ELEMENT_SELECTOR);
}

class FocusLocker {
    private currentLockedParams?: ILockedParams;
    private readonly lockedItemsStack: ILockedParams[] = [];

    lock(container: HTMLElement) {
        // Store previous locked params
        let prevParams = this.currentLockedParams || this.getDefaultParams();
        prevParams && this.lockedItemsStack.push(prevParams);
        this.lockFocus(container);
    }

    unlock() {
        this.unlockLast();
    }

    private getDefaultParams() {
        let currentActiveElement = document.activeElement;
        return currentActiveElement && { activeElement: currentActiveElement as HTMLElement };
    }

    private trapTabKey(container: HTMLElement, focusableElements: NodeListOf<Element>) {
        if (focusableElements.length === 0) {
            return;
        }
        const firstElement = focusableElements[0];
        const lastElement = focusableElements[focusableElements.length - 1];

        function keydownHandler(e) {
          // Check if pressed key is Tab
          if (e.key === "Tab") {
            // If the shift key is pressed for reverse navigation
            if (e.shiftKey) {
              if (document.activeElement === firstElement) {
                e.preventDefault();
                (lastElement as HTMLElement).focus();
              }
            } else {
              // Forward tab navigation
              if (document.activeElement === lastElement) {
                e.preventDefault();
                (firstElement as HTMLElement).focus();
              }
            }
          }
        }
        container.addEventListener("keydown", keydownHandler);
    }

    private lockFocus(container: HTMLElement, activeElement?: HTMLElement) {
        this.toggleNeighbours(true);

        let focusableItems = getFocusableItems(container);
        let isActiveElementFocusable = [].some.call(focusableItems, el => el === activeElement);

        let currentLockedParams = {
            container,
            activeElement: (isActiveElementFocusable ? activeElement : focusableItems[0]) as HTMLElement | undefined
        };
        this.currentLockedParams = currentLockedParams;

        currentLockedParams.activeElement?.focus();

        // Keep the user in the dialog when the tab is pressed
        this.trapTabKey(container, focusableItems);

        // Add aria-hidden for main app container
        this.toggleNeighbours(false);
    }

    private toggleNeighbours(show: boolean) {
        let currentContainer = this.currentLockedParams?.container;
        if (currentContainer) {
            let nieghbours = currentContainer.parentElement?.children;
            if (nieghbours) {
                let isNeighbourFit = (neighbour: HTMLElement) => neighbour !== currentContainer && NEIGHBOURS_TAGS_TO_EXCLUDE.indexOf(neighbour.tagName) === -1;
                [].forEach.call(nieghbours, (neighbour: HTMLElement) => {
                    if (isNeighbourFit(neighbour)) {
                        let initialAreaHidden = neighbour.getAttribute(INITIAL_ARIA_HIDDEN_ATTR) || neighbour.getAttribute("aria-hidden") || "unset";
                        if (!show) {
                            neighbour.setAttribute("aria-hidden", "true");
                            neighbour.setAttribute(INITIAL_ARIA_HIDDEN_ATTR, initialAreaHidden);
                        } else {
                            if (initialAreaHidden === "unset") {
                                neighbour.removeAttribute("aria-hidden");
                            } else {
                                neighbour.setAttribute("aria-hidden", initialAreaHidden);
                            }
                            neighbour.removeAttribute(INITIAL_ARIA_HIDDEN_ATTR);
                        }
                    }
                });
            }
        }
    }

    private unlockLast() {
        let prevLockedParams = this.lockedItemsStack.pop();
        this.toggleNeighbours(true);
        this.currentLockedParams = undefined;
        if (!prevLockedParams) {
            return;
        }
        if (prevLockedParams?.container) {
            this.lockFocus(prevLockedParams.container, prevLockedParams.activeElement);
        } else {
            prevLockedParams.activeElement?.focus();
        }
    }
}

export let focusLocker = new FocusLocker();